/**
 * StateBadge — colored status pill with hover/focus tooltip explaining state
 * semantics and transitions out.
 *
 * Phase 8 Prompt 2 — per audit deliverable 04B Section J + 04D Section B
 * universal status color system. The 11 supported states match 04B Section F
 * State Badge Tooltip Catalog.
 *
 * Color/icon mapping is sourced from help-content.json so visual changes are
 * a content edit, not a code change.
 *
 * Usage:
 *   <StateBadge state="active" />
 *   <StateBadge state="paused" size="medium" />
 */

import React from "react";
import { Chip, Tooltip, Box } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ErrorIcon from "@mui/icons-material/Error";
import ArchiveIcon from "@mui/icons-material/Archive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useHelpContent } from "../../contexts/HelpContentContext";

// Material icon name → component map (registry value uses the string name).
const ICON_MAP: Record<string, React.ComponentType<{ sx?: object }>> = {
  play_arrow: PlayArrowIcon,
  pause: PauseIcon,
  error: ErrorIcon,
  archive: ArchiveIcon,
  check_circle: CheckCircleIcon,
  schedule: ScheduleIcon,
  send: SendIcon,
  circle: CircleOutlinedIcon,
  logout: LogoutIcon,
};

export interface StateBadgeProps {
  /** State key — looks up content.stateBadges[state] (e.g. 'active', 'paused'). */
  state: string;
  /** Pill size. Default: "small". */
  size?: "small" | "medium";
  /** Override icon visibility (default: true). */
  showIcon?: boolean;
}

export const StateBadge: React.FC<StateBadgeProps> = ({
  state,
  size = "small",
  showIcon = true,
}) => {
  const { getStateBadge } = useHelpContent();
  const entry = getStateBadge(state);

  if (!entry) {
    // Render a neutral pill so the row doesn't break visually.
    return (
      <Chip
        label={state}
        size={size}
        sx={{ bgcolor: "grey.300", color: "text.primary" }}
      />
    );
  }

  const IconComponent = entry.icon ? ICON_MAP[entry.icon] : null;
  // Brand colors are dark (high-contrast); use white foreground.
  const isDarkBg = entry.color !== "#D4AC0D"; // Warm Gold reads better on ink.
  const fgColor = isDarkBg ? "#FFFFFF" : "#1B1F23";

  const tooltipTitle = (
    <Box sx={{ p: 0.5 }}>
      <strong>{entry.label}</strong>
      <br />
      {entry.semantic}
      {entry.transitionOut && (
        <>
          <br />
          <em style={{ fontSize: "0.85em" }}>{entry.transitionOut}</em>
        </>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <Chip
        label={entry.label}
        size={size}
        icon={
          showIcon && IconComponent ? (
            <IconComponent sx={{ color: `${fgColor} !important` }} />
          ) : undefined
        }
        sx={{
          bgcolor: entry.color,
          color: fgColor,
          fontWeight: 600,
          fontFamily: '"Montserrat", sans-serif',
          fontSize: size === "small" ? "0.7rem" : "0.85rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          "& .MuiChip-icon": {
            color: `${fgColor} !important`,
            fontSize: size === "small" ? "0.9rem" : "1.1rem",
          },
        }}
        aria-label={`Status: ${entry.label}. ${entry.semantic}`}
      />
    </Tooltip>
  );
};

export default StateBadge;
